/*
 * 작성자 : 김광민
 * 날짜 : 2024.07.04
 * 기능 : 상황판에서 가로 사이즈 5 기상종합 컴포넌트
 */

import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import WeatherTotalNow from './WeatherTotalNow';
import WeatherTotalTime from './WeatherTotalTime';
import WeatherTotalWeek from './WeatherTotalWeek';
import { PulseLoader } from 'react-spinners';
import useWeatherInfo from '../../hooks/useWeatherInfo';
import IssueGuide from '../IssueGuide';
import { WEATHER_ERROR_MESSAGE } from '../../_constants';

const Root = styled.div`
  height: inherit;
  display: grid !important;
  overflow: hidden;

  .weatherError {
    position: absolute;
    height: auto;
    width: 100%;
    > div {
      width: inherit;
      > p {
        display: flex;
        align-items: center;
        justify-content: center;
        width: inherit;
        padding: 0.25rem 0;
        color: #fff;
        background-color: rgba(0, 0, 0, 0.4);
        border-radius: 0;
        gap: 0.25rem;

        > span.material-symbols-rounded {
          font-size: 20px;
        }
      }
    }
  }

  &.small {
    background: var(--weather-bg) !important;
  }

  &.large {
    background: linear-gradient(90deg, var(--weather-bg) 0%, var(--weather-bg-2) 100%) !important;

    @media screen and (max-width: 768px) {
      & .time-weather-wrap {
        display: none;
      }
    }

    @media screen and (max-width: 480px) {
      & .large-weather-bottom {
        display: none !important;
      }
    }
  }

  > div {
    display: flex;
    height: 100%;
    align-items: center;
    justify-content: center;
  }
  &.grid-cols-3 {
    grid-template-columns: repeat(3, 1fr);
  }
  &.grid-cols-2 {
    grid-template-columns: repeat(2, 1fr);
  }
  &.grid-cols-1 {
    grid-template-columns: repeat(1, 1fr);
  }
  &.hide-child-1 > div:nth-child(1) {
    display: none;
  }
  &.hide-child-2 > div:nth-child(2) {
    display: none;
  }
  &.hide-child-3 > div:nth-child(3) {
    display: none;
  }
  &.loading {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    background-color: var(--primary);
  }
`;

// 6일간의 날씨 데이터를 초기화
const days = (length: number) =>
  Array.from({ length }).map((_, index) => {
    const targetDay = dayjs().add(index + 1, 'day'); // +1 to start from tomorrow
    return {
      order: index,
      date: targetDay.date(),
      day: targetDay.format('ddd'),
      sky: '-',
      maxTemp: '0',
      minTemp: '0',
    };
  });

// 시간별 날씨 데이터를 초기화 현재 시간에서 8시간 뒤까지
const times = Array.from({ length: 8 }).map((_, index) => {
  const targetTime = dayjs().add(index + 1, 'hour');
  return {
    date: targetTime.format('YYYYMMDD'),
    time: `${targetTime.format('HH')}00`,
    pcp: '0',
    pop: '0',
    sky: '-',
    sno: '-',
    tmp: '0',
    wsd: '0',
  };
});

// 현재 시간에 따른 상태 반환
const getTimeStatus = (): string => {
  const currentHour = dayjs().hour();
  if (currentHour >= 0 && currentHour < 6) return 'morning';
  if (currentHour >= 6 && currentHour < 18) return 'afternoon';
  return 'evening';
};

const WeatherTotal = ({ combination, autoRefresh, size = 'small' }: { combination: { time: boolean; week: boolean }; autoRefresh: boolean; size?: 'small' | 'large' }) => {
  const {
    curWeather,
    weekWeather,
    threeDaysWeather,
    openDayWeather,
    openThreeDaysWeather,
    tWeather,
    shortAdrs,
    isErrorCurrentWeather,
    isErrorThreeDaysWeather,
    isErrorWeekWeather,
    isErrorTimeWeather,
  } = useWeatherInfo({ autoRefresh });

  // 로컬 스토리지에서 데이터 가져오기
  const after6DaysLocalStorage = window.localStorage.getItem('after6Days');
  const currentWeatherLocalStorage = window.localStorage.getItem('currentWeather');
  const timeWeatherLocalStorage = window.localStorage.getItem('timeWeather');
  const after2DaysLocalStorage = window.localStorage.getItem('after2Days');

  const localAfter6Days = after6DaysLocalStorage //
    ? JSON.parse(after6DaysLocalStorage)
    : days(6);

  const localCurrentWeather = currentWeatherLocalStorage //
    ? JSON.parse(currentWeatherLocalStorage)
    : { wsd: '0', tmp: '0', minTemp: '0', maxTemp: '0', rainPct: '0', sky: '-', pm10: '0', pm25: '0' };

  const localTimeWeather = timeWeatherLocalStorage //
    ? JSON.parse(timeWeatherLocalStorage)
    : times;

  const localAfter2Days = after2DaysLocalStorage //
    ? JSON.parse(after2DaysLocalStorage)
    : days(3);

  const [after6Days, setAfter6Days] = useState<IDays[]>(localAfter6Days);
  const [currentWeather, setCurrentWeather] = useState(localCurrentWeather);
  const [timeWeather, setTimeWeather] = useState(localTimeWeather);
  const [after2Days, setAfter2Days] = useState<any[]>(localAfter2Days);
  const [width, setWidth] = useState(window.innerWidth);
  const [autoPlay, setAutoPlay] = useState(false);
  const [hideColumn, setHideColumn] = useState<number>(0);
  const [hideChild, setHideChild] = useState<string>('');

  useEffect(() => {
    if (currentWeather) {
      window.localStorage.setItem('currentWeather', JSON.stringify(currentWeather));
    }
  }, [currentWeather]);

  useEffect(() => {
    if (!isErrorCurrentWeather && curWeather) {
      const { sky, tmp, wsd } = curWeather;
      setCurrentWeather((prev: any) => ({ ...prev, sky, tmp, wsd }));
    }
  }, [isErrorCurrentWeather, curWeather]);

  useEffect(() => {
    if (!isErrorThreeDaysWeather && threeDaysWeather) {
      setAfter2Days(threeDaysWeather);
    }
  }, [isErrorThreeDaysWeather, threeDaysWeather]);

  useEffect(() => {
    if (isErrorThreeDaysWeather && openDayWeather) {
      const { sky, tmp, wsd, todayTmn, todayTmx } = openDayWeather;
      setCurrentWeather((prev: any) => ({ ...prev, sky, tmp, wsd, minTemp: todayTmn, maxTemp: todayTmx }));
    }
  }, [isErrorThreeDaysWeather, openDayWeather]);

  useEffect(() => {
    if (isErrorThreeDaysWeather && openThreeDaysWeather) {
      setAfter2Days(openThreeDaysWeather);
    }
  }, [isErrorThreeDaysWeather, openThreeDaysWeather]);

  useEffect(() => {
    if (after2Days) {
      window.localStorage.setItem('after2Days', JSON.stringify(after2Days));
    }
  }, [after2Days]);

  useEffect(() => {
    if (weekWeather) {
      const status = getTimeStatus();
      setCurrentWeather((prev: any) => ({
        ...prev,
        minTemp: weekWeather.todayTmn,
        maxTemp: weekWeather.todayTmx,
        rainPct: status === 'morning' ? weekWeather.morPop : status === 'evening' ? weekWeather.dinPop : weekWeather.aftPop,
      }));

      setAfter6Days(weekWeather.week);
    }
  }, [weekWeather]);

  // 시간별 날씨 쿼리 결과 처리
  useEffect(() => {
    if (tWeather) {
      const { pm10, pm25, weatherList } = tWeather;
      setCurrentWeather((prev: any) => ({ ...prev, pm10, pm25 }));
      setTimeWeather(weatherList);
      window.localStorage.setItem('timeWeather', JSON.stringify(weatherList));
    }
  }, [tWeather]);

  // 기존 날씨 주간 API 쿼리 에러시 시간 업데이트
  useEffect(() => {
    if (isErrorWeekWeather) {
      // 로컬 스토리지에 값이 있으면 그 값을 활용
      if (after6DaysLocalStorage) {
        const localData = JSON.parse(after6DaysLocalStorage);
        const dateSet = days(6).map((day) => {
          const findIdx = localData.findIndex((localDay: any) => localDay.date === day.date);
          return findIdx !== -1 ? { ...day, sky: localData[findIdx].sky, minTemp: localData[findIdx].minTemp, maxTemp: localData[findIdx].maxTemp } : day;
        });
        setAfter6Days(dateSet);
      } else {
        // 로컬 스토리지 값이 없으면 시간 업데이트
        setAfter6Days(days(6));
      }
    }
  }, [isErrorWeekWeather]);

  // 기존 날씨 시간별 API 에러시 시간 업데이트
  useEffect(() => {
    if (isErrorTimeWeather) {
      setTimeWeather(times);
      window.localStorage.setItem('timeWeather', JSON.stringify(times));
    }
  }, [isErrorTimeWeather]);

  useEffect(() => {
    if (after6Days) {
      window.localStorage.setItem('after6Days', JSON.stringify(after6Days));
    }
  }, [after6Days]);

  useEffect(() => {
    const handleLargeResize = () => {
      const weatherTotalLarge = document.querySelector('.weather-total-wrap.large') as HTMLDivElement;
      const timeWeatherWrap = weatherTotalLarge && (weatherTotalLarge.querySelector('.time-weather-wrap') as HTMLDivElement);

      // 1300px 이상일 경우 잘리는 부분 보완
      if (weatherTotalLarge && weatherTotalLarge.clientWidth <= 1300) {
        weatherTotalLarge.style.gridTemplateColumns = 'repeat(1, 1fr)';
        timeWeatherWrap && (timeWeatherWrap.style.display = 'none');
      } else {
        weatherTotalLarge.style.gridTemplateColumns = 'repeat(2, 1fr)';
        timeWeatherWrap && (timeWeatherWrap.style.display = 'flex');
      }
    };

    if (size === 'large') {
      window.addEventListener('load', handleLargeResize);
      window.addEventListener('resize', handleLargeResize);
    }

    return () => {
      window.removeEventListener('load', handleLargeResize);
      window.removeEventListener('resize', handleLargeResize);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);

    const { time, week } = combination;
    const timeOnly = time && !week;
    const weekOnly = !time && week;
    if (timeOnly || weekOnly) {
      if (width < 768) {
        setAutoPlay(true);
      } else {
        setAutoPlay(false);
      }
    } else if (time && week) {
      if (width < 1280) {
        setAutoPlay(true);
      } else {
        setAutoPlay(false);
      }
    } else {
      setAutoPlay(false);
    }
    return () => window.removeEventListener('resize', handleResize);
  }, [window.innerWidth]);

  useEffect(() => {
    let intervalId: any;

    if (size === 'small' && autoPlay) {
      intervalId = setInterval(() => {
        const { time, week } = combination;
        trasitionColumn(time && week, hideColumn, width); // 특정 함수 a 호출
      }, 2000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoPlay, hideColumn, width]);

  const trasitionColumn = (isThreeColumn: boolean, column: number, w: number) => {
    if (isThreeColumn) {
      setHideColumn(column >= 3 ? 1 : column + 1);
      if (w < 1280 && w >= 768) {
        setHideChild(
          column >= 3 //
            ? 'hide-child-1'
            : `hide-child-${column + 1}`
        );
      } else {
        setHideChild(
          column >= 3 //
            ? 'hide-child-1 hide-child-2'
            : column === 2
            ? 'hide-child-2 hide-child-3'
            : 'hide-child-3 hide-child-1'
        );
      }
    }
    if (!isThreeColumn) {
      if (column === 1) {
        setHideColumn(2);
        setHideChild('hide-child-2');
      } else {
        setHideColumn(1);
        setHideChild('hide-child-1');
      }
    }
  };

  const getGrid = ({ time, week }: { time: boolean; week: boolean }) => {
    const timeOnly = time && !week;
    const weekOnly = !time && week;
    const timeOnlyLarge = time && !week && size === 'large';
    if (timeOnly || weekOnly || timeOnlyLarge) {
      if (width >= 768) return 'grid-cols-2';
      return 'grid-cols-1';
    }
    if (width >= 1280) return 'grid-cols-3';
    if (width >= 768) return 'grid-cols-2';
    return 'grid-cols-1';
  };

  if (timeWeather.length === 0) {
    return (
      <Root className='loading'>
        <div>
          <PulseLoader color='rgb(255, 255, 255)' size='8px' />
        </div>
      </Root>
    );
  }

  // if (isErrorCurrentWeather || isErrorWeekWeather || isErrorThreeDaysWeather || isErrorTimeWeather) return <IssueGuide isImg={false} text={WEATHER_ERROR_MESSAGE} />;

  return (
    <Root
      className={`weather-total-wrap ${getGrid(combination)} ${autoPlay ? hideChild : ''} ${size}`}
      style={isErrorCurrentWeather || isErrorWeekWeather || isErrorThreeDaysWeather || isErrorTimeWeather ? { position: 'relative' } : { position: 'static' }}
    >
      {/* {isErrorCurrentWeather || isErrorWeekWeather || isErrorThreeDaysWeather || isErrorTimeWeather ? (
        <div className='weatherError'>
          <IssueGuide isImg={false} text={WEATHER_ERROR_MESSAGE} icon='report' />
        </div>
      ) : null} */}
      <WeatherTotalNow //
        currentWeather={currentWeather}
        after2Days={after2Days}
        shortAdrs={shortAdrs || ''}
        size={size}
      />
      {combination.time ? (
        <WeatherTotalTime //
          timeWeather={timeWeather}
          size={size}
        />
      ) : null}
      {combination.week ? (
        <WeatherTotalWeek //
          after6Days={after6Days}
          size={size}
        />
      ) : null}
    </Root>
  );
};

export default WeatherTotal;
